import { computed, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { queryKeys } from '@/queries/_shared/queryKeys';
import http from '@/services/http';

function unwrapBody(data) {
  if (data && typeof data === 'object' && 'body' in data) return data.body;
  return data;
}

function resolvePupilId(inputPupilId) {
  const direct = Number(inputPupilId || 0);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const fromStorage = Number(localStorage.getItem('p_id') || 0);
  if (Number.isFinite(fromStorage) && fromStorage > 0) return fromStorage;

  const currentChildUin = String(localStorage.getItem('currentChildUin') || '').trim();
  if (currentChildUin) {
    try {
      const childList = JSON.parse(localStorage.getItem('childList') || '[]');
      if (Array.isArray(childList)) {
        const selected = childList.find((child) => String(child?.uin || '') === currentChildUin);
        const fromChild = Number(selected?.pupil_id || selected?._data?.pupil_id || 0);
        if (Number.isFinite(fromChild) && fromChild > 0) return fromChild;
      }
    } catch {
      // ignore storage parse errors
    }
  }

  try {
    const person = JSON.parse(localStorage.getItem('person') || '{}');
    const fromProfile = Number(person?.profile?.pupil_id || person?._data?.pupil_id || 0);
    if (Number.isFinite(fromProfile) && fromProfile > 0) return fromProfile;
  } catch {
    // ignore storage parse errors
  }

  return 0;
}

async function fetchCalendarPlans(startTs, endTs, groupId) {
  const response = await http.get('/lplans', {
    params: {
      start_ts: Number(startTs || 0),
      end_ts: Number(endTs || 0),
      group_id: Number(groupId || 0),
      expand: 'hometask',
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchCalendarMarks(startTs, endTs, pupilId) {
  const resolvedPupilId = resolvePupilId(pupilId);
  const response = await http.get('/marks/current', {
    params: {
      start_ts: Number(startTs || 0),
      end_ts: Number(endTs || 0),
      expand: 'lplan,parent_response',
      p_id: resolvedPupilId,
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchCalendarHolidays(startTs, endTs) {
  const response = await http.get('/scheduler/calendar', {
    params: {
      start_ts: Number(startTs || 0),
      end_ts: Number(endTs || 0)
    }
  });
  return unwrapBody(response.data) || [];
}

async function fetchCalendarSubjectsByIds(ids = []) {
  const normalizedIds = ids
    .map((id) => Number(id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!normalizedIds.length) return [];

  const response = await http.get('/subjects', {
    params: {
      ids: normalizedIds
    }
  });
  return unwrapBody(response.data) || [];
}

export function useCalendarPlansQuery(startTs, endTs, groupId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.calendar.plans(toValue(startTs), toValue(endTs), toValue(groupId))),
    queryFn: () => fetchCalendarPlans(toValue(startTs), toValue(endTs), toValue(groupId)),
    enabled: computed(
      () =>
        Number(toValue(startTs) || 0) > 0 &&
        Number(toValue(endTs) || 0) > 0 &&
        Number(toValue(groupId) || 0) > 0
    ),
    ...options
  });
}

export function useCalendarMarksQuery(startTs, endTs, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.calendar.marks(toValue(startTs), toValue(endTs), toValue(pupilId))),
    queryFn: () => fetchCalendarMarks(toValue(startTs), toValue(endTs), toValue(pupilId)),
    enabled: computed(() => Number(toValue(startTs) || 0) > 0 && Number(toValue(endTs) || 0) > 0),
    ...options
  });
}

export function useCalendarHolidaysQuery(startTs, endTs, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.calendar.holidays(toValue(startTs), toValue(endTs))),
    queryFn: () => fetchCalendarHolidays(toValue(startTs), toValue(endTs)),
    enabled: computed(() => Number(toValue(startTs) || 0) > 0 && Number(toValue(endTs) || 0) > 0),
    ...options
  });
}

export function useCalendarSubjectsByIdsQuery(ids, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.calendar.subjectsByIds(toValue(ids) || [])),
    queryFn: () => fetchCalendarSubjectsByIds(toValue(ids) || []),
    enabled: computed(() => Array.isArray(toValue(ids)) && toValue(ids).length > 0),
    ...options
  });
}

export {
  fetchCalendarHolidays,
  fetchCalendarMarks,
  fetchCalendarPlans,
  fetchCalendarSubjectsByIds
};
